/**
 * The Suno models a Vault song can be tagged as made with: the numbered versions and the custom models.
 * A song can carry several (e.g. the same song re-rendered on v5.5 and v6), stored on DraftVault.sunoModels
 * as a comma-separated list of the ids below. Shared by the client pills and the server's validation.
 *
 * Stored format: ids joined by "," with no spaces, in the display order of SUNO_MODELS (null when none).
 * Ids are fixed lowercase slugs from this list, so they never contain a comma and need no escaping; the
 * route only accepts ids from this list, and parseSunoModels drops anything else on read. With every model
 * selected the value is 87 characters, comfortably inside the VARCHAR(255) column (a test guards this).
 */

export type SunoModelGroup = 'version' | 'custom';

export interface SunoModel {
  id: string;
  label: string;
  group: SunoModelGroup;
}

export const SUNO_MODELS: readonly SunoModel[] = [
  { id: 'v4', label: 'v4', group: 'version' },
  { id: 'v4.5', label: 'v4.5', group: 'version' },
  { id: 'v4.5-plus', label: 'v4.5+', group: 'version' },
  { id: 'v5', label: 'v5', group: 'version' },
  { id: 'v5.5', label: 'v5.5', group: 'version' },
  { id: 'v6-mini', label: 'v6-mini', group: 'version' },
  { id: 'v6', label: 'v6', group: 'version' },
  { id: 'v6-wild', label: 'v6-wild', group: 'version' },
  { id: 'scooby-vanzant', label: 'Scooby VanZant', group: 'custom' },
  { id: 'marcus-steel', label: 'Marcus Steel', group: 'custom' },
  { id: 'smoke-mccreedy', label: 'Smoke McCreedy', group: 'custom' },
] as const;

export const SUNO_MODEL_IDS: readonly string[] = SUNO_MODELS.map(m => m.id);

const ORDER = new Map(SUNO_MODEL_IDS.map((id, i) => [id, i]));

/** Reads the stored comma-separated value into valid ids, in display order, ignoring anything unknown. */
export function parseSunoModels(stored: string | null | undefined): string[] {
  if (!stored) return [];
  const seen = new Set<string>();
  for (const part of stored.split(',')) {
    const id = part.trim();
    if (ORDER.has(id)) seen.add(id);
  }
  return [...seen].sort((a, b) => ORDER.get(a)! - ORDER.get(b)!);
}

/** Turns a selection into the stored value: valid ids only, de-duplicated, in display order; null when empty. */
export function serializeSunoModels(ids: readonly string[]): string | null {
  const clean = parseSunoModels(ids.join(','));
  return clean.length ? clean.join(',') : null;
}

/** Adds the id if it is not selected, removes it if it is. Unknown ids are ignored. */
export function toggleSunoModel(current: readonly string[], id: string): string[] {
  if (!ORDER.has(id)) return [...current];
  return current.includes(id) ? current.filter(x => x !== id) : parseSunoModels([...current, id].join(','));
}

export function sunoModelLabel(id: string): string {
  return SUNO_MODELS.find(m => m.id === id)?.label ?? id;
}

/**
 * The models a song can be GENERATED with now, as Suno's `mv`. Suno has retired the older models (v3 to
 * v5.5), so only the v6 family is offered; the older ones stay in SUNO_MODELS and pillIdForSunoModel so
 * songs and clips made on them can still be tagged. Codenames were read from suno.com's own client code
 * (2026-10-09); that the v6-wild and v6-mini codenames are accepted by the generate call is taken from the
 * page's own selector, not from a test generation. `short` is the label used on compact pickers.
 */
export interface SunoGenerationModel {
  value: string;
  label: string;
  short: string;
  pill: string;
}

export const SUNO_GENERATION_MODELS: readonly SunoGenerationModel[] = [
  { value: 'chirp-hawk', label: 'Suno V6 (chirp-hawk)', short: 'v6', pill: 'v6' },
  { value: 'chirp-hawk-wild', label: 'Suno V6 Wild (chirp-hawk-wild)', short: 'v6-wild', pill: 'v6-wild' },
  { value: 'chirp-goose', label: 'Suno V6 Mini (chirp-goose)', short: 'v6-mini', pill: 'v6-mini' },
];

export const DEFAULT_SUNO_MODEL = 'chirp-hawk';

/**
 * A custom model is generated with `mv: "chirp-custom:<custom_model_id>"` and nothing else model-specific
 * (confirmed from a real generate request captured on suno.com, 2026-10-09). The id is a UUID that belongs
 * to the account, so it is read from the account's model list, never hard-coded.
 */
const CUSTOM_MV = /^chirp-custom:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isCustomModelMv(value: string | null | undefined): boolean {
  return !!value && CUSTOM_MV.test(value);
}

/**
 * The model to generate with: the first candidate that is a model still in use (the v6 family, or a
 * well-formed custom model), else the default. Saved settings from before the older models were retired
 * (chirp-fenix, chirp-v3.5 ...) therefore fall back to v6 instead of sending a request Suno will refuse.
 */
export function resolveSunoGenerationModel(...candidates: Array<string | null | undefined>): string {
  for (const c of candidates) {
    if (c && (SUNO_GENERATION_MODELS.some(m => m.value === c) || isCustomModelMv(c))) return c;
  }
  return DEFAULT_SUNO_MODEL;
}

const squash = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');

/** The Vault pill for a custom model, matched by name ignoring spacing ("Scooby Van Zant" = "Scooby VanZant"). */
export function pillIdForCustomModelName(name: string | null | undefined): string | null {
  if (!name) return null;
  const wanted = squash(name);
  return SUNO_MODELS.find(m => m.group === 'custom' && squash(m.label) === wanted)?.id ?? null;
}

/**
 * Which Vault pill a generation with this model should add to the song. A v6-family model maps to its
 * pill; a custom model maps to the custom pill with the same name. Suno's feed does NOT say which custom
 * model a clip used (it reports only "Custom"), so this send-time choice is the only place to know.
 */
export function pillIdForGeneration(mv: string, customModelName?: string | null): string | null {
  return isCustomModelMv(mv) ? pillIdForCustomModelName(customModelName) : pillIdForSunoModel(mv);
}

/**
 * Which pill a Suno model name belongs to, or null when it has none (v3, or a codename we don't know).
 * Accepts what Suno's clips actually carry: codenames (chirp-crow), codename variants (chirp-auk-turbo),
 * legacy ids (chirp-v4-5, chirp-v4-5-plus) and plain display names (v5.5, V4.5+). The codename checks use
 * substrings, like Suno's own client does, and test "wild" before plain "hawk".
 */
export function pillIdForSunoModel(name: string | null | undefined): string | null {
  const s = (name ?? '').trim().toLowerCase();
  if (!s) return null;
  if (s.includes('hawk')) return s.includes('wild') ? 'v6-wild' : 'v6';
  if (s.includes('goose')) return 'v6-mini';
  if (s.includes('fenix')) return 'v5.5';
  if (s.includes('crow')) return 'v5';
  if (s.includes('bluejay')) return 'v4.5-plus';
  if (s.includes('auk')) return 'v4.5';

  const v = s.replace(/^chirp-/, '').replace(/_/g, '-').replace(/\s+/g, '-');
  if (/^v4[-.]?5[-.]?(plus|\+)$/.test(v)) return 'v4.5-plus';
  if (/^v4[-.]5$/.test(v)) return 'v4.5';
  if (/^v4(-tau)?$/.test(v)) return 'v4';
  if (/^v5[-.]5$/.test(v)) return 'v5.5';
  if (/^v5$/.test(v)) return 'v5';
  if (/^v6-?mini$/.test(v)) return 'v6-mini';
  if (/^v6-?wild$/.test(v)) return 'v6-wild';
  if (/^v6$/.test(v)) return 'v6';
  return null;
}

/** Adds `modelId` to a stored value without disturbing what is already there (manual picks included). */
export function addSunoModel(stored: string | null | undefined, modelId: string): string | null {
  return serializeSunoModels([...parseSunoModels(stored), modelId]);
}
